import React, { PropsWithChildren, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  useTheme,
} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import {
  VpBorderRadius,
  vpBoxShadow,
} from '../../features/variety-planner/styles';

const makeStyles = (theme: Theme) => ({
  customDialog: {
    marginTop: '120px',
    marginBottom: '40px',
    marginX: '24px',
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 34, 60, 0.4)',
    },
    '& .MuiDialog-paper': {
      borderRadius: VpBorderRadius.LARGE,
      boxShadow: vpBoxShadow,
      overflow: 'hidden',
    },
  },
  customDialogTitle: {
    padding: theme.spacing(1.75),
    borderBottom: `1px solid ${theme.palette.tableDivider}`,
  },
  customDialogTitleWithCloseBtn: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  customDialogContent: {
    padding: 0,
    overflow: 'auto',

    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: VpBorderRadius.SMALL,
      marginRight: '10px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: VpBorderRadius.SMALL,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },

    scrollbarWidth: 'thin',
    scrollbarColor: '#888 #f1f1f1',
  },
  closeIconBtn: {
    padding: 0,
    '& .MuiSvgIcon-root': {
      color: theme.palette.questionsForm.formLightGrey,
      '&:hover': {
        color: theme.palette.questionsForm.formGrey,
      },
    },
  },
});

interface AppDialogProps {
  isOpen: boolean;
  title: ReactNode;
  displayCloseButton?: boolean;
  handleClose: () => void;
}

const AppDialog: React.FC<Readonly<PropsWithChildren<AppDialogProps>>> = ({
  isOpen,
  children,
  title,
  displayCloseButton = false,
  handleClose,
}) => {
  const theme = useTheme();
  const styles = makeStyles(theme);

  return (
    <Dialog
      fullScreen
      open={isOpen}
      sx={styles.customDialog}
      onClose={handleClose}
    >
      <DialogTitle
        sx={{
          ...styles.customDialogTitle,
          ...(displayCloseButton && styles.customDialogTitleWithCloseBtn),
        }}
      >
        {title}
        {displayCloseButton && (
          <IconButton
            disableRipple
            sx={styles.closeIconBtn}
            onClick={handleClose}
          >
            <CancelOutlinedIcon sx={{ fontSize: '32px' }} />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent sx={styles.customDialogContent}>{children}</DialogContent>
    </Dialog>
  );
};

export default AppDialog;
